import { CSSProperties } from "react";
import { useTheme } from "theme/useTheme";
import { DARK_VARIANTS, ThemePreference } from "theme/theme";

// Sun / moon / monitor glyphs (inherit currentColor)
const SunIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
  </svg>
);
const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

const OPTIONS: {
  value: ThemePreference;
  label: string;
  icon: JSX.Element;
}[] = [
  { value: "light", label: "Light", icon: <SunIcon /> },
  { value: "dark", label: "Dark", icon: <MoonIcon /> },
];

// Stacked: "Theme" label on its own line, then a full-width segmented control.
const containerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  width: "100%",
  gap: 6,
};

const groupStyle: CSSProperties = {
  display: "flex",
  width: "100%",
  border: "1px solid rgba(128,128,128,0.35)",
  borderRadius: 8,
  overflow: "hidden",
};

const segmentStyle = (active: boolean): CSSProperties => ({
  flex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  padding: "5px 4px",
  fontSize: "0.78rem",
  fontWeight: active ? 600 : 500,
  border: "none",
  cursor: "pointer",
  // Follows the same fill/label pair a primary button uses, so the softer
  // variants get their pale fill with a dark label instead of a dark-blue block
  // sitting in a pale menu. The fallbacks are the light-mode values.
  background: active ? "var(--dark-accent, #4a68e8)" : "transparent",
  // Both halves are tokenized. The inactive label sits on the menu panel, and
  // a fixed grey that reads fine on near-black drops to 3.18:1 on sage.
  color: active
    ? "var(--dark-accent-on, #fff)"
    : "var(--dark-text-muted, #8a8a8a)",
});

// Colour-variant swatches, shown only while dark is actually in effect - the
// choice is meaningless in light, and showing five dark swatches on a light
// page invites people to click them and see nothing happen.
// Two rows of five rather than one row of ten. Ten swatches side by side in a
// menu this narrow leaves each one too small to read a gradient from, and the
// wrap also puts each softer variant directly under the darker one it mirrors,
// which is the clearest way to show they are the same five casts twice.
const swatchRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  width: "100%",
};

// A single near-black square tells you nothing - all five variants would look
// identical. Each swatch instead shows the variant's range across its three
// structural tones (canvas -> panel -> raised), which is what actually differs
// between them and makes the cast visible at 22px.
const swatchStyle = (
  variant: { swatch: string; panel: string; raised: string },
  active: boolean
): CSSProperties => ({
  // 5 per row: a fifth of the width, less its share of the 4 gaps between them.
  flex: "0 0 calc(20% - 4.8px)",
  height: 24,
  borderRadius: 6,
  cursor: "pointer",
  background: `linear-gradient(135deg, ${variant.swatch} 0%, ${variant.swatch} 34%, ${variant.panel} 34%, ${variant.panel} 67%, ${variant.raised} 67%)`,
  // The active swatch is ringed in the accent; the rest get a hairline so the
  // darkest variants still read against the menu.
  //
  // The border WIDTH stays 1px either way, and only its colour changes. These
  // are flex items with `flex-basis: 0`, which cannot shrink below their own
  // border box - so a 2px border on the selected one made it 2px wider than
  // its neighbours and slid every swatch after it along by 2px, and the row
  // twitched left and right as you picked different colours. box-shadow is
  // drawn outside layout, so the ring can carry the emphasis for free.
  // The ring is the only cue for which of ten swatches is active, so it follows
  // the accent's TEXT token rather than its fill: against the menu panel the
  // fill drops to 2.32:1 on sage, where the text token holds 5.23:1 or better
  // on every variant.
  // Longhands, not the `border` shorthand: the width has to stay provably
  // identical across all ten swatches, and a var() inside the shorthand leaves
  // the width to a parser that may or may not recover it.
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: active
    ? "var(--dark-accent-text, #4a68e8)"
    : "rgba(128,128,128,0.45)",
  boxShadow: active
    ? "0 0 0 2px var(--dark-accent-ring, rgba(74,104,232,0.55))"
    : "none",
  padding: 0,
});

const ThemeToggle = () => {
  const {
    preference,
    resolvedTheme,
    darkVariant,
    setPreference,
    setDarkVariant,
  } = useTheme();
  return (
    <div style={containerStyle}>
      <span>Theme</span>
      <div
        style={groupStyle}
        // clicks inside stay in the dropdown
        onClick={(e) => e.stopPropagation()}
      >
        {OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            style={segmentStyle(preference === option.value)}
            title={option.label}
            aria-pressed={preference === option.value}
            onClick={(e) => {
              e.stopPropagation();
              setPreference(option.value);
            }}
          >
            {option.icon}
            {option.label}
          </button>
        ))}
      </div>
      {resolvedTheme === "dark" && (
        <div
          style={swatchRowStyle}
          onClick={(e) => e.stopPropagation()}
          data-testid="dark-variant-picker"
        >
          {DARK_VARIANTS.map((variant) => (
            <button
              key={variant.value}
              type="button"
              style={swatchStyle(variant, darkVariant === variant.value)}
              title={variant.label}
              aria-label={variant.label}
              aria-pressed={darkVariant === variant.value}
              data-testid={`dark-variant-${variant.value}`}
              onClick={(e) => {
                e.stopPropagation();
                setDarkVariant(variant.value);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
